import { useAccountContext } from '@seek/hirer-graphql-react';
import type { TeamMemberForJobAccess } from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Notice, Strong, Text, TextLink, Inline } from 'braid-design-system';
import type React from 'react';

import { useConfig } from '../../../../hooks/useConfig';
import { USER_MANAGEMENT } from '../links';

import translations from './.vocab';

interface Props {
  teamMembers: TeamMemberForJobAccess[];
}

export const AddTeamMembersCallout: React.FC<
  React.PropsWithChildren<Props>
> = ({ teamMembers }: Props) => {
  const { urlResolver } = useConfig();
  let linkHref = USER_MANAGEMENT;

  const { accountContext } = useAccountContext();

  const advertiserName = accountContext?.currentAdvertiser?.name ?? '';
  const { t } = useTranslations(translations);
  if (!accountContext?.currentAdvertiser?.permissionOptions.canManageUsers) {
    const adminUsersEmail = teamMembers
      .filter((teamMember) => teamMember.isAdmin)
      .map((teamMember) => teamMember.email)
      .join(',');
    const emailSubject = t('Request to add user to our...', { advertiserName })
      .split(' ')
      .join('%20');
    linkHref = `mailto:${adminUsersEmail}?subject=${emailSubject}`;
  }

  const linkText = accountContext?.currentAdvertiser?.permissionOptions
    .canManageUsers
    ? t('Add team members now.')
    : t('Request access from your admin.');

  return (
    <Notice tone="promote">
      <Inline space="xsmall" align={['right', 'left']} collapseBelow="tablet">
        <Text>{t('Don’t see who you are looking for?')}</Text>
        <Text>
          <Strong>
            <TextLink
              href={urlResolver({ path: linkHref })}
              target="_blank"
              rel="noopener"
            >
              {linkText}
            </TextLink>
          </Strong>
        </Text>
      </Inline>
    </Notice>
  );
};
